import { useState } from "react";
import { useForm, yupResolver } from "@mantine/form";
import { Modal, Group, Button, Text, PasswordInput } from "@mantine/core";
import { UserManagement } from "src/interfaces";
import { updatePasswordSchema } from "src/validations";

interface Props {
  opened: boolean;
  onClose: () => void;
  user?: UserManagement | null;
  onUpdate: (password: string) => Promise<void> | void;
}

const UpdatePasswordModal = ({ opened, onClose, user, onUpdate }: Props) => {
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const form = useForm({
    initialValues: { password: "" },
    validate: yupResolver(updatePasswordSchema),
    validateInputOnChange: true,
    validateInputOnBlur: true,
  });

  const handleUpdate = form.onSubmit(async (values) => {
    if (!user) return;
    setError(null);
    setLoading(true);
    try {
      await onUpdate(values.password);
      form.reset();
      onClose();
    } catch (e) {
      setError("Could not update password");
    } finally {
      setLoading(false);
    }
  });

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={`Update password`}
      centered
      zIndex={2100}
    >
      <div className="flex flex-col gap-3">
        <Text size="sm" color="dimmed">
          Enter a new password for the user. Minimum 8 characters, must include
          uppercase, lowercase, numbers, and special characters (e.g., Pa$$w0rd).
        </Text>
        <PasswordInput
          label="New Password"
          placeholder="********"
          {...form.getInputProps("password")}
        />
        {error && (
          <Text color="red" size="sm">
            {error}
          </Text>
        )}
        <Group position="right">
          <Button
            variant="default"
            onClick={() => {
              form.reset();
              onClose();
            }}
            disabled={loading}
          >
            Cancel
          </Button>
          <Button color="dark" onClick={() => handleUpdate()} loading={loading}>
            Update
          </Button>
        </Group>
      </div>
    </Modal>
  );
};

export default UpdatePasswordModal;
